import { CircleAlert, CircleCheck, Info, TriangleAlert, WifiOff, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { classes } from '../outils/classes';
import styles from './Bandeau.module.css';

export type TypeBandeau = 'succes' | 'erreur' | 'info' | 'attention' | 'horsligne';

const ICONES = { succes: CircleCheck, erreur: CircleAlert, info: Info, attention: TriangleAlert, horsligne: WifiOff };

export interface ProprietesBandeau {
  type: TypeBandeau;
  titre?: ReactNode;
  children?: ReactNode;
  onFermer?: () => void;
  /**
   * Disparition automatique (succès) après ce délai en millisecondes ; suspendue tant que le
   * pointeur ou le focus est sur le bandeau, pour rester lisible.
   */
  dureeMs?: number;
  className?: string;
}

export const TEXTE_HORS_LIGNE = 'Hors connexion : vos saisies sont enregistrées sur cet appareil';

export function Bandeau({ type, titre, children, onFermer, dureeMs, className }: ProprietesBandeau) {
  const Icone = ICONES[type];
  const [suspendu, setSuspendu] = useState(false);
  const rappel = useRef(onFermer);
  rappel.current = onFermer;
  useEffect(() => {
    if (!dureeMs || suspendu) return;
    const m = window.setTimeout(() => rappel.current?.(), dureeMs);
    return () => window.clearTimeout(m);
  }, [dureeMs, suspendu]);

  return (
    <div
      className={classes(styles.bandeau, styles[type], className)}
      role={type === 'erreur' ? 'alert' : 'status'}
      onMouseEnter={() => setSuspendu(true)}
      onMouseLeave={() => setSuspendu(false)}
      onFocus={() => setSuspendu(true)}
      onBlur={() => setSuspendu(false)}
    >
      <Icone aria-hidden strokeWidth={1.75} />
      <div className={styles.texte}>
        {titre ? <span className={styles.titre}>{titre}</span> : null}
        {type === 'horsligne' && !children ? <span>{TEXTE_HORS_LIGNE}</span> : children}
      </div>
      {onFermer ? (
        <button type="button" className={styles.fermer} onClick={onFermer} aria-label="Fermer le message">
          <X aria-hidden strokeWidth={1.75} width={18} height={18} />
        </button>
      ) : null}
    </div>
  );
}
